/**
 * Draft.js DOMObserver.registerMutation does:
 *   nullthrows(findAncestorOffsetKey(mutation.target))
 *
 * Decorator remounts (e.g. LanguageTool underlines), suggestion UI, or browser
 * extensions can emit MutationObserver records whose target has no
 * data-offset-key ancestor. That throws "Got unexpected null or undefined"
 * and crashes the editor.
 *
 * Skip those mutations instead of throwing — Draft already ignores other
 * unreadable records by returning null from getMutationTextContent.
 */

// @ts-expect-error draft-js has no types for this internal path
import DOMObserver from 'draft-js/lib/DOMObserver.js';
// @ts-expect-error draft-js has no types for this internal path
import findAncestorOffsetKey from 'draft-js/lib/findAncestorOffsetKey.js';

type MutationLike = {
    type: string;
    target: Node;
    removedNodes?: NodeList;
};

type DomObserverInstance = {
    getMutationTextContent: (mutation: MutationLike) => string | null | undefined;
    mutations: { set: (key: string, value: string) => { set: (key: string, value: string) => unknown } };
};

let patched = false;

/** Pure helper used by the patch and by unit tests. */
export const registerMutationSafely = (
    observer: DomObserverInstance,
    mutation: MutationLike,
    findKey: (node: Node) => string | null | undefined = findAncestorOffsetKey,
): void => {
    const textContent = observer.getMutationTextContent(mutation);
    if (textContent == null)
        return;

    const offsetKey = findKey(mutation.target);
    if (offsetKey == null)
        return;

    observer.mutations = observer.mutations.set(offsetKey, textContent) as DomObserverInstance['mutations'];
};

export const patchDraftJsDomObserver = (): boolean => {
    if (patched)
        return false;

    const proto = DOMObserver?.prototype;
    if (!proto || typeof proto.registerMutation !== 'function')
        return false;

    proto.registerMutation = function registerMutation(this: DomObserverInstance, mutation: MutationLike) {
        registerMutationSafely(this, mutation);
    };
    patched = true;
    return true;
};

export default patchDraftJsDomObserver;
